import React, { useState } from 'react';
import { Plus, Save, X, Upload, Check, Cloud } from 'lucide-react';
import firebaseMetadataService from '../services/firebaseMetadataService';
import VercelBlobUploader from './VercelBlobUploader';

const QuickProductAdmin = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showVercelUploader, setShowVercelUploader] = useState(false);
  const [formData, setFormData] = useState({
    imageName: '',
    title: '',
    description: '',
    category: '',
    price: '',
    inStock: true,
    featured: false
  });

  const categories = [
    'Arduino & Development Boards',
    'Sensors & Modules',
    'Display & Interface Modules',
    'Communication Modules',
    'Motors & Motor Drivers',
    'Relay Modules & Power Control',
    'Power Supply & Voltage Regulators',
    'Breadboards, Kits & Misc',
    'Other Components'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      // Validate required fields
      if (!formData.imageName || !formData.title || !formData.category) {
        throw new Error('Please fill in all required fields');
      }

      // Save metadata to Firebase
      await firebaseMetadataService.saveImageMetadata(formData.imageName, {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: formData.price ? parseFloat(formData.price) : null,
        in_stock: formData.inStock,
        featured: formData.featured
      });

      setMessage({ type: 'success', text: 'Product added successfully! It will appear in the shop automatically.' });
      
      // Reset form
      setFormData({
        imageName: '',
        title: '',
        description: '',
        category: '',
        price: '',
        inStock: true,
        featured: false
      });

      // Close after 2 seconds
      setTimeout(() => {
        setIsOpen(false);
        setMessage(null);
      }, 2000);

    } catch (error) {
      console.error('Error adding product:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) {
    return (
      <>
        <div className="fixed bottom-4 right-4 flex flex-col space-y-3 z-50">
          <button
            onClick={() => setShowVercelUploader(true)}
            className="bg-green-600 text-white p-4 rounded-full shadow-lg hover:bg-green-700 transition-colors"
            title="Upload to Vercel Blob (Recommended)"
          >
            <Cloud size={24} />
          </button>
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
            title="Quick Add Product (Supabase)"
          >
            <Plus size={24} />
          </button>
        </div>

        {showVercelUploader && (
          <VercelBlobUploader
            onClose={() => setShowVercelUploader(false)}
            onUploadComplete={(results) => {
              console.log('Upload completed:', results);
              setShowVercelUploader(false);
              // Optionally refresh the page or update state
              window.location.reload();
            }}
          />
        )}
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Quick Add Product</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Image Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image Filename * <span className="text-xs text-gray-500">(e.g., arduino-uno.jpg)</span>
            </label>
            <input
              type="text"
              value={formData.imageName}
              onChange={(e) => handleInputChange('imageName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="product-image.jpg"
              required
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Arduino Uno R3"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select a category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price (Kwacha)
            </label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => handleInputChange('price', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="750"
              min="0"
              step="0.01"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Product description..."
            />
          </div>

          {/* Checkboxes */}
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.inStock}
                onChange={(e) => handleInputChange('inStock', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">In Stock</span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => handleInputChange('featured', e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Featured Product</span>
            </label>
          </div>

          {/* Message */}
          {message && (
            <div className={`p-3 rounded-lg flex items-center ${
              message.type === 'success' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {message.type === 'success' ? (
                <Check className="mr-2" size={16} />
              ) : (
                <X className="mr-2" size={16} />
              )}
              {message.text}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Adding Product...
              </>
            ) : (
              <>
                <Save className="mr-2" size={16} />
                Add Product
              </>
            )}
          </button>
        </form>

        {/* Instructions */}
        <div className="p-4 bg-gray-50 border-t text-xs text-gray-600">
          <p><strong>Note:</strong> Make sure the image file exists in your Supabase storage under the 'products' folder before adding the product metadata.</p>
        </div>
      </div>
    </div>
  );
};

export default QuickProductAdmin;
